import { Color, type AnyColor } from "@repo/app-types/constants/colors";
import type { FC, ReactNode } from "react";
import { Button } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { tv } from "tailwind-variants";
import { Size } from "../helpers/helpers.js";

/** The colors a pill has styles for. */
export const allPillColors = [
  Color.Red,
  Color.DarkRed,
  Color.Orange,
  Color.Amber,
  Color.Yellow,
  Color.Lime,
  Color.Green,
  Color.DarkEmerald,
  Color.Emerald,
  Color.DarkerBlue,
  Color.DarkBlue,
  Color.Blue,
  Color.LightBlue,
  Color.Gray,
] as const;

export type PillColor = (typeof allPillColors)[number];

export function getPillColor(color: PillColor): PillColor;
export function getPillColor(color: AnyColor): PillColor | undefined;
export function getPillColor(color: AnyColor): PillColor | undefined {
  return allPillColors.find((pillColor) => pillColor === color);
}

interface PillProps {
  label: ReactNode;
  size: Size;
  color: PillColor;
  onClick?: () => void;
  className?: string;
}

export const pillStyle = tv({
  base: "inline-flex h-fit items-center overflow-hidden rounded-md font-medium whitespace-nowrap ring-1 ring-inset",
  variants: {
    isClickable: {
      true: "cursor-pointer",
    },
    size: {
      [Size.SMALL]: "px-2 py-1 text-xs",
      [Size.MEDIUM]: "px-3 py-2 text-sm",
      [Size.LARGE]: "px-5 py-2 text-lg",
    },
    color: {
      [Color.Blue]: "border-blue-600 bg-blue-50 text-blue-700 ring-blue-600/20",
      [Color.DarkerBlue]: "bg-blue-700 text-blue-50 ring-blue-900/20",
      [Color.DarkBlue]: "bg-blue-500 text-blue-50 ring-blue-800/20",
      [Color.LightBlue]:
        "border-blue-400 bg-blue-50 text-blue-500 ring-blue-400/20",
      [Color.Green]: "bg-green-50 text-green-700 ring-green-600/20",
      [Color.Red]: "bg-red-50 text-red-700 ring-red-600/20",
      [Color.Yellow]: "bg-yellow-50 text-yellow-700 ring-yellow-600/20",
      [Color.Gray]: "bg-gray-50 text-gray-700 ring-gray-600/20",
      [Color.Lime]: "bg-lime-50 text-lime-700 ring-lime-600/20",
      [Color.Emerald]: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
      [Color.DarkEmerald]:
        "bg-emerald-200 text-emerald-900 ring-emerald-800/20",
      [Color.DarkRed]: "bg-red-200 text-red-900 ring-red-800/20",
      [Color.Amber]: "bg-amber-50 text-amber-700 ring-amber-600/20",
      [Color.Orange]: "bg-orange-50 text-orange-700 ring-orange-600/20",
    },
  },
  compoundVariants: [
    {
      isClickable: true,
      color: Color.Blue,
      class: "hover:bg-blue-100",
    },
    {
      isClickable: true,
      color: Color.DarkerBlue,
      class: "hover:bg-blue-400",
    },
    {
      isClickable: true,
      color: Color.DarkBlue,
      class: "hover:bg-blue-300",
    },
    {
      isClickable: true,
      color: Color.LightBlue,
      class: "hover:bg-blue-100",
    },
    {
      isClickable: true,
      color: Color.Green,
      class: "hover:bg-green-100",
    },
    {
      isClickable: true,
      color: Color.Red,
      class: "hover:bg-red-100",
    },
    {
      isClickable: true,
      color: Color.Yellow,
      class: "hover:bg-yellow-100",
    },
    {
      isClickable: true,
      color: Color.Gray,
      class: "hover:bg-gray-100",
    },
    {
      isClickable: true,
      color: Color.Lime,
      class: "hover:bg-lime-100",
    },
    {
      isClickable: true,
      color: Color.Emerald,
      class: "hover:bg-emerald-100",
    },
    {
      isClickable: true,
      color: Color.DarkEmerald,
      class: "hover:bg-emerald-300",
    },
    {
      isClickable: true,
      color: Color.DarkRed,
      class: "hover:bg-red-300",
    },
    {
      isClickable: true,
      color: Color.Amber,
      class: "hover:bg-amber-100",
    },
    {
      isClickable: true,
      color: Color.Orange,
      class: "hover:bg-orange-100",
    },
  ],
});

export const Pill: FC<PillProps> = ({
  label,
  size,
  color,
  onClick,
  className,
}) => {
  const baseClassName = pillStyle({ size, color, isClickable: !!onClick });
  const mergedClassName = twMerge(baseClassName, className);

  if (onClick) {
    return (
      <Button className={mergedClassName} onClick={onClick} type="button">
        {label}
      </Button>
    );
  }

  return <div className={mergedClassName}>{label}</div>;
};
